/**
 * Iter 68i — Vendor Fleet Bulk-Edit dialog.
 *
 * One-click "apply this change to N cars" tool for vendors with large
 * fleets. Two filter modes:
 *   • By condition (make / type / price band) — vendor types a few
 *     constraints, frontend previews the matches before apply.
 *   • By explicit selection (checkbox column in the cars list).
 *
 * Bulk-safe field list mirrors the backend whitelist:
 *   Mileage policy, free cancellation hours, no-credit-card,
 *   pet-friendly, smoking, active/paused, cleaning fee, late-return grace.
 *
 * Why these and not "every field"? Images, locations, insurance plans
 * need per-car attention — bulk-flipping them would surface confusion
 * and support tickets. The whitelist is intentionally tight.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Layers, Filter, CheckSquare, Loader2, Sparkles, AlertTriangle, Wand2, Clock, Calendar } from "lucide-react";

const MILEAGE_OPTIONS = [
  { value: "", label: "— Don't change —" },
  { value: "unlimited", label: "Unlimited mileage" },
  { value: "limited", label: "Limited (set km/day)" },
];

export default function VendorBulkEditDialog({ open, onOpenChange, cars, onApplied }) {
  // ─────────── Filter mode ─────────────────────────────────────
  const [mode, setMode] = useState("filter"); // 'filter' | 'select'
  const [make, setMake] = useState("");
  const [carType, setCarType] = useState("");
  const [priceLte, setPriceLte] = useState("");
  const [priceGte, setPriceGte] = useState("");
  const [pickedIds, setPickedIds] = useState(() => new Set());

  // ─────────── Changes payload (only keys with `enabled` flip get sent) ─────────
  const [enabled, setEnabled] = useState({
    mileage_policy: false,
    free_cancellation_hours: false,
    no_credit_card_required: false,
    pet_friendly: false,
    smoking_allowed: false,
    is_active: false,
    cleaning_fee: false,
    late_return_grace_minutes: false,
  });
  const [values, setValues] = useState({
    mileage_policy: "unlimited",
    mileage_limit_km_per_day: 300,
    mileage_extra_km_rate: 0.15,
    free_cancellation_hours: 48,
    no_credit_card_required: true,
    pet_friendly: true,
    smoking_allowed: false,
    is_active: true,
    cleaning_fee: 0,
    late_return_grace_minutes: 30,
  });

  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  // Iter 68j — schedule mode + datetime picker.
  const [runMode, setRunMode] = useState("now"); // 'now' | 'later'
  const [runAt, setRunAt] = useState(() => {
    // Default to "in 1 hour" local time so the datetime-local input
    // shows something useful when the operator first toggles to later.
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16); // 'YYYY-MM-DDTHH:mm'
  });
  const [scheduleLabel, setScheduleLabel] = useState("");

  // Reset picked-ids when the dialog opens so prior selections don't linger.
  useEffect(() => {
    if (open) {
      setPickedIds(new Set());
      setPreview(null);
    }
  }, [open]);

  // Build the filter object the backend expects.
  const builtFilter = useMemo(() => {
    if (mode === "select") return { ids: Array.from(pickedIds) };
    return {
      make: make.trim() || null,
      car_type: carType.trim() || null,
      price_lte: priceLte ? Number(priceLte) : null,
      price_gte: priceGte ? Number(priceGte) : null,
    };
  }, [mode, make, carType, priceLte, priceGte, pickedIds]);

  const builtChanges = useMemo(() => {
    const out = {};
    if (enabled.mileage_policy) {
      out.mileage_policy = values.mileage_policy || null;
      if (values.mileage_policy === "limited") {
        out.mileage_limit_km_per_day = Number(values.mileage_limit_km_per_day) || null;
        out.mileage_extra_km_rate = Number(values.mileage_extra_km_rate) || null;
      }
    }
    if (enabled.free_cancellation_hours) out.free_cancellation_hours = Number(values.free_cancellation_hours);
    if (enabled.no_credit_card_required) out.no_credit_card_required = !!values.no_credit_card_required;
    if (enabled.pet_friendly) out.pet_friendly = !!values.pet_friendly;
    if (enabled.smoking_allowed) out.smoking_allowed = !!values.smoking_allowed;
    if (enabled.is_active) out.is_active = !!values.is_active;
    if (enabled.cleaning_fee) out.cleaning_fee = Number(values.cleaning_fee);
    if (enabled.late_return_grace_minutes) out.late_return_grace_minutes = Number(values.late_return_grace_minutes);
    return out;
  }, [enabled, values]);

  const enabledCount = Object.values(enabled).filter(Boolean).length;
  const filterCount = mode === "select"
    ? pickedIds.size
    : [make, carType, priceLte, priceGte].filter(Boolean).length;

  const previewMatches = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/vendor/cars/bulk-preview", { filter: builtFilter });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const apply = async () => {
    if (enabledCount === 0) {
      toast.error("Toggle at least one change above before applying.");
      return;
    }
    const total = preview?.total ?? 0;
    if (runMode === "now" && total === 0) {
      toast.error("No cars match this filter — preview first.");
      return;
    }
    if (runMode === "later") {
      // Validate the future timestamp client-side too.
      const target = new Date(runAt);
      if (Number.isNaN(target.getTime()) || target <= new Date()) {
        toast.error("Pick a future date and time.");
        return;
      }
      if (!window.confirm(`Schedule ${enabledCount} change${enabledCount === 1 ? "" : "s"} to run at ${target.toLocaleString()}? You can cancel any time before then.`)) return;
      setApplying(true);
      try {
        await api.post("/vendor/cars/bulk-schedule", {
          filter: builtFilter,
          changes: builtChanges,
          run_at: target.toISOString(),
          label: scheduleLabel.trim() || null,
        });
        toast.success(`Scheduled · runs at ${target.toLocaleString()}`);
        onApplied?.();
        onOpenChange(false);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setApplying(false);
      }
      return;
    }

    if (!window.confirm(`Apply ${enabledCount} change${enabledCount === 1 ? "" : "s"} to ${total} car${total === 1 ? "" : "s"}? This can't be undone in bulk.`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/vendor/cars/bulk-update", {
        filter: builtFilter,
        changes: builtChanges,
      });
      toast.success(`Bulk update applied · ${data.updated}/${data.matched} car${data.matched === 1 ? "" : "s"}`);
      onApplied?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  const togglePick = (id) => {
    setPickedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vendor-bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center ring-1 ring-amber-200">
              <Wand2 className="h-5 w-5" />
            </span>
            <div>
              <div className="font-display text-xl">Bulk edit fleet</div>
              <div className="text-xs text-neutral-500 font-normal">
                Apply one change to many cars in seconds. Real customer bookings are never touched.
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* ───── Step 1: pick the cars ─────────────────────── */}
        <section className="rounded-2xl border border-neutral-200 p-4">
          <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-3 flex items-center gap-1.5">
            <Filter className="h-3 w-3" /> Step 1 · Pick the cars
          </div>
          <Tabs value={mode} onValueChange={(v) => { setMode(v); setPreview(null); }}>
            <TabsList className="grid grid-cols-2 max-w-sm">
              <TabsTrigger value="filter" data-testid="vendor-bulk-mode-filter">
                <Filter className="h-3 w-3 mr-1" /> By condition
              </TabsTrigger>
              <TabsTrigger value="select" data-testid="vendor-bulk-mode-select">
                <CheckSquare className="h-3 w-3 mr-1" /> Hand-pick ({pickedIds.size})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="filter" className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label htmlFor="bulk-make" className="text-[11px]">Make</Label>
                <Input id="bulk-make" value={make} onChange={(e) => setMake(e.target.value)} placeholder="e.g. Audi" className="h-9 text-sm" data-testid="vendor-bulk-filter-make" />
              </div>
              <div>
                <Label htmlFor="bulk-type" className="text-[11px]">Car type</Label>
                <Input id="bulk-type" value={carType} onChange={(e) => setCarType(e.target.value)} placeholder="e.g. SUV" className="h-9 text-sm" data-testid="vendor-bulk-filter-type" />
              </div>
              <div>
                <Label htmlFor="bulk-pgte" className="text-[11px]">Min €/day</Label>
                <Input id="bulk-pgte" type="number" value={priceGte} onChange={(e) => setPriceGte(e.target.value)} placeholder="0" className="h-9 text-sm" data-testid="vendor-bulk-filter-pgte" />
              </div>
              <div>
                <Label htmlFor="bulk-plte" className="text-[11px]">Max €/day</Label>
                <Input id="bulk-plte" type="number" value={priceLte} onChange={(e) => setPriceLte(e.target.value)} placeholder="999" className="h-9 text-sm" data-testid="vendor-bulk-filter-plte" />
              </div>
            </TabsContent>
            <TabsContent value="select" className="mt-3">
              <div className="border border-neutral-200 rounded-xl max-h-56 overflow-y-auto" data-testid="vendor-bulk-pick-list">
                {(cars || []).length === 0 ? (
                  <div className="p-6 text-center text-sm text-neutral-500">No cars yet — add some first.</div>
                ) : (
                  <ul className="divide-y divide-neutral-100">
                    {cars.map((c) => (
                      <li key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-amber-50/40">
                        <input
                          type="checkbox"
                          checked={pickedIds.has(c.id)}
                          onChange={() => togglePick(c.id)}
                          className="h-4 w-4 rounded border-neutral-300 text-amber-500"
                          data-testid={`vendor-bulk-pick-${c.id}`}
                        />
                        <div className="flex-1 text-sm">
                          <span className="font-medium">{c.make} {c.model}</span>{" "}
                          <span className="text-neutral-500">· {c.year} · {c.car_type || "—"} · €{c.price_per_day}/day</span>
                          {c.is_active === false && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-100 ring-1 ring-rose-200 px-1.5 py-0.5 rounded-full">Paused</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setPickedIds(new Set((cars || []).map((c) => c.id)))} data-testid="vendor-bulk-pick-all">
                  Select all
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setPickedIds(new Set())} data-testid="vendor-bulk-pick-none">
                  Clear
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <Button type="button" variant="outline" size="sm" onClick={previewMatches} disabled={previewLoading || filterCount === 0} className="rounded-full" data-testid="vendor-bulk-preview-btn">
              {previewLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
              Preview matching cars
            </Button>
            {preview && (
              <div className="text-sm" data-testid="vendor-bulk-preview-result">
                <span className="font-semibold text-amber-700">{preview.total}</span> car{preview.total === 1 ? "" : "s"} match
                {preview.total > 0 && preview.total <= 12 && (
                  <span className="text-neutral-500 ml-2 text-xs">
                    {preview.rows.slice(0, 12).map((r) => `${r.make} ${r.model}`).join(", ")}
                  </span>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ───── Step 2: pick the changes ──────────────────── */}
        <section className="rounded-2xl border border-neutral-200 p-4 mt-3">
          <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-3 flex items-center gap-1.5">
            <Layers className="h-3 w-3" /> Step 2 · Pick the changes
          </div>
          <div className="space-y-2">
            <BulkRow
              label="Mileage policy"
              enabled={enabled.mileage_policy}
              onToggle={(v) => setEnabled((s) => ({ ...s, mileage_policy: v }))}
              testId="vendor-bulk-row-mileage"
            >
              <select
                value={values.mileage_policy}
                onChange={(e) => setValues((s) => ({ ...s, mileage_policy: e.target.value }))}
                disabled={!enabled.mileage_policy}
                className="h-8 rounded-md border border-neutral-200 bg-white px-2 text-sm disabled:bg-neutral-100"
                data-testid="vendor-bulk-mileage-policy"
              >
                {MILEAGE_OPTIONS.filter((o) => o.value !== "").map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {enabled.mileage_policy && values.mileage_policy === "limited" && (
                <>
                  <Input
                    type="number"
                    value={values.mileage_limit_km_per_day}
                    onChange={(e) => setValues((s) => ({ ...s, mileage_limit_km_per_day: e.target.value }))}
                    className="h-8 w-24 text-sm"
                    placeholder="km/day"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={values.mileage_extra_km_rate}
                    onChange={(e) => setValues((s) => ({ ...s, mileage_extra_km_rate: e.target.value }))}
                    className="h-8 w-24 text-sm"
                    placeholder="€/extra km"
                  />
                </>
              )}
            </BulkRow>

            <BulkRow
              label="Free cancellation"
              enabled={enabled.free_cancellation_hours}
              onToggle={(v) => setEnabled((s) => ({ ...s, free_cancellation_hours: v }))}
              testId="vendor-bulk-row-free-cancel"
            >
              <Input
                type="number"
                value={values.free_cancellation_hours}
                onChange={(e) => setValues((s) => ({ ...s, free_cancellation_hours: e.target.value }))}
                disabled={!enabled.free_cancellation_hours}
                className="h-8 w-24 text-sm"
              />
              <span className="text-xs text-neutral-500">hours before pickup</span>
            </BulkRow>

            <BulkRow
              label="No credit card required"
              enabled={enabled.no_credit_card_required}
              onToggle={(v) => setEnabled((s) => ({ ...s, no_credit_card_required: v }))}
              testId="vendor-bulk-row-no-card"
            >
              <Switch
                checked={!!values.no_credit_card_required}
                onCheckedChange={(v) => setValues((s) => ({ ...s, no_credit_card_required: v }))}
                disabled={!enabled.no_credit_card_required}
                data-testid="vendor-bulk-no-card-val"
              />
              <span className="text-xs text-neutral-500">{values.no_credit_card_required ? "Allowed (badge shown)" : "Card required"}</span>
            </BulkRow>

            <BulkRow
              label="Pet-friendly"
              enabled={enabled.pet_friendly}
              onToggle={(v) => setEnabled((s) => ({ ...s, pet_friendly: v }))}
              testId="vendor-bulk-row-pets"
            >
              <Switch checked={!!values.pet_friendly} onCheckedChange={(v) => setValues((s) => ({ ...s, pet_friendly: v }))} disabled={!enabled.pet_friendly} />
            </BulkRow>

            <BulkRow
              label="Smoking allowed"
              enabled={enabled.smoking_allowed}
              onToggle={(v) => setEnabled((s) => ({ ...s, smoking_allowed: v }))}
              testId="vendor-bulk-row-smoking"
            >
              <Switch checked={!!values.smoking_allowed} onCheckedChange={(v) => setValues((s) => ({ ...s, smoking_allowed: v }))} disabled={!enabled.smoking_allowed} />
            </BulkRow>

            <BulkRow
              label="Active (visible on marketplace)"
              enabled={enabled.is_active}
              onToggle={(v) => setEnabled((s) => ({ ...s, is_active: v }))}
              testId="vendor-bulk-row-active"
            >
              <Switch checked={!!values.is_active} onCheckedChange={(v) => setValues((s) => ({ ...s, is_active: v }))} disabled={!enabled.is_active} />
              <span className="text-xs text-neutral-500">{values.is_active ? "Active" : "Paused"}</span>
            </BulkRow>

            <BulkRow
              label="Cleaning fee (€)"
              enabled={enabled.cleaning_fee}
              onToggle={(v) => setEnabled((s) => ({ ...s, cleaning_fee: v }))}
              testId="vendor-bulk-row-cleaning"
            >
              <Input
                type="number"
                value={values.cleaning_fee}
                onChange={(e) => setValues((s) => ({ ...s, cleaning_fee: e.target.value }))}
                disabled={!enabled.cleaning_fee}
                className="h-8 w-24 text-sm"
              />
            </BulkRow>

            <BulkRow
              label="Late-return grace (minutes)"
              enabled={enabled.late_return_grace_minutes}
              onToggle={(v) => setEnabled((s) => ({ ...s, late_return_grace_minutes: v }))}
              testId="vendor-bulk-row-grace"
            >
              <Input
                type="number"
                value={values.late_return_grace_minutes}
                onChange={(e) => setValues((s) => ({ ...s, late_return_grace_minutes: e.target.value }))}
                disabled={!enabled.late_return_grace_minutes}
                className="h-8 w-24 text-sm"
              />
            </BulkRow>
          </div>
        </section>

        {/* Safety reminder */}
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Bulk changes can't be reversed in one click — each car keeps its own audit row.
            Use "Preview matching cars" above to confirm the count before applying.
          </span>
        </div>

        {/* ───── Step 3 (optional): schedule for later ─────── */}
        <section className="rounded-2xl border border-neutral-200 p-4 mt-3" data-testid="vendor-bulk-schedule">
          <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-3 flex items-center gap-1.5">
            <Clock className="h-3 w-3" /> Step 3 (optional) · When should it run?
          </div>
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <button
              type="button"
              onClick={() => setRunMode("now")}
              className={`px-3 py-2 rounded-xl border text-sm font-medium transition flex items-center justify-center gap-2 ${runMode === "now" ? "border-amber-400 bg-amber-50 text-amber-900" : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300"}`}
              data-testid="vendor-bulk-runmode-now"
            >
              <Wand2 className="h-3.5 w-3.5" /> Run now
            </button>
            <button
              type="button"
              onClick={() => setRunMode("later")}
              className={`px-3 py-2 rounded-xl border text-sm font-medium transition flex items-center justify-center gap-2 ${runMode === "later" ? "border-amber-400 bg-amber-50 text-amber-900" : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300"}`}
              data-testid="vendor-bulk-runmode-later"
            >
              <Calendar className="h-3.5 w-3.5" /> Schedule
            </button>
          </div>
          {runMode === "later" && (
            <div className="mt-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                <div>
                  <Label htmlFor="bulk-runat" className="text-[11px]">Run at (your local time)</Label>
                  <Input
                    id="bulk-runat"
                    type="datetime-local"
                    value={runAt}
                    onChange={(e) => setRunAt(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="vendor-bulk-runat"
                  />
                </div>
                <div>
                  <Label htmlFor="bulk-label" className="text-[11px]">Label (optional)</Label>
                  <Input
                    id="bulk-label"
                    value={scheduleLabel}
                    onChange={(e) => setScheduleLabel(e.target.value)}
                    placeholder="e.g. Christmas pause, June price bump"
                    maxLength={120}
                    className="h-9 text-sm"
                    data-testid="vendor-bulk-label"
                  />
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 leading-relaxed">
                The match-set is computed at <strong>run time</strong>, not now — useful if your fleet changes between now and then.
                You can cancel the job any time before it runs from the "Scheduled changes" list on the My cars tab.
              </p>
            </div>
          )}
        </section>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={applying} data-testid="vendor-bulk-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={apply}
            disabled={applying || enabledCount === 0 || (runMode === "now" && !preview?.total)}
            className="rounded-full bg-amber-500 hover:bg-amber-400 text-amber-950"
            data-testid="vendor-bulk-apply"
          >
            {applying ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : runMode === "later" ? <Calendar className="h-3.5 w-3.5 mr-1" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />}
            {runMode === "later" ? "Schedule" : "Apply"} {enabledCount > 0 ? `${enabledCount} change${enabledCount === 1 ? "" : "s"}` : "changes"}
            {runMode === "now" && preview?.total ? ` to ${preview.total} car${preview.total === 1 ? "" : "s"}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkRow({ label, enabled, onToggle, children, testId }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2 transition ${enabled ? "border-amber-200 bg-amber-50/50" : "border-neutral-200 bg-white"}`} data-testid={testId}>
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        className="data-[state=checked]:bg-amber-500"
        data-testid={`${testId}-toggle`}
      />
      <span className={`text-sm font-medium min-w-[200px] ${enabled ? "text-neutral-900" : "text-neutral-500"}`}>
        {label}
      </span>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}
